import { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Divider, FormControl, FormHelperText, FormLabel,
    Input, Option, Select, Sheet, Stack, Typography,
} from '@mui/joy';

import AdScheduleEditor from './AdScheduleEditor';
import { useAdsPlacements } from './useAdsData';
import {
    OBJECTIVE_LABELS, PRICING_LABELS, STRATEGY_BY_PRICING, STRATEGY_LABELS, formatMoney,
} from './adsConstants';


/** ISO-строка сервера → значение для datetime-local. */
const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (value) => (value ? new Date(value).toISOString() : null);


/**
 * Форма настроек кампании. Используется мастером (шаги 1–2)
 * и вкладкой «Настройки» в карточке.
 *
 * `lockedFields` приходит из карточки работающей кампании: сервер
 * разрешает менять у неё только бюджет, ставки, расписание и дату окончания.
 */
export default function AdCampaignForm({
    value, onChange, limits, selectedPlacements = [], lockedFields = [], showSchedule = true,
}) {
    const { placements } = useAdsPlacements();
    const v = value || {};

    const locked = (field) => lockedFields.includes(field);

    const set = (patch) => onChange({ ...v, ...patch });

    // Стратегия обязана соответствовать модели оплаты — сервер это проверяет
    useEffect(() => {
        const allowed = STRATEGY_BY_PRICING[v.pricing_model] || [];
        if (v.bid_strategy && !allowed.includes(v.bid_strategy)) {
            set({ bid_strategy: allowed[0] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [v.pricing_model]);

    const isAuto = String(v.bid_strategy || '').startsWith('auto_');
    const isCpc = v.pricing_model === 'cpc';

    const minBid = useMemo(() => {
        const field = isCpc ? 'min_cpc' : 'min_cpm';
        const chosen = placements.filter((p) => selectedPlacements.includes(p.code));
        if (!chosen.length) {
            return Number(isCpc ? limits?.min_cpc : limits?.min_cpm) || 0;
        }
        return Math.max(...chosen.map((p) => Number(p[field] || 0)));
    }, [placements, selectedPlacements, isCpc, limits]);

    const minDaily = Number(limits?.min_daily_budget || 0);

    return (
        <Stack spacing={2}>
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 1.5 }}>Основное</Typography>

                <Stack spacing={1.5}>
                    <FormControl>
                        <FormLabel>Название кампании</FormLabel>
                        <Input
                            value={v.name || ''}
                            onChange={(e) => set({ name: e.target.value })}
                            placeholder="Например: Промо весна, Москва"
                        />
                    </FormControl>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Цель</FormLabel>
                            <Select
                                value={v.objective || 'traffic'}
                                onChange={(_, val) => val && set({ objective: val })}
                                disabled={locked('objective')}
                            >
                                {Object.entries(OBJECTIVE_LABELS)
                                    .filter(([key]) => key !== 'conversions')
                                    .map(([key, label]) => (
                                        <Option key={key} value={key}>{label}</Option>
                                    ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Модель оплаты</FormLabel>
                            <Select
                                value={v.pricing_model || 'cpc'}
                                onChange={(_, val) => val && set({ pricing_model: val })}
                                disabled={locked('pricing_model')}
                            >
                                {Object.entries(PRICING_LABELS).map(([key, label]) => (
                                    <Option key={key} value={key}>{label}</Option>
                                ))}
                            </Select>
                            {locked('pricing_model') && (
                                <FormHelperText>
                                    Нельзя менять у работающей кампании
                                </FormHelperText>
                            )}
                        </FormControl>
                    </Stack>

                    <FormControl>
                        <FormLabel>Стратегия назначения ставок</FormLabel>
                        <Select
                            value={v.bid_strategy || 'manual_cpc'}
                            onChange={(_, val) => val && set({ bid_strategy: val })}
                        >
                            {(STRATEGY_BY_PRICING[v.pricing_model || 'cpc'] || []).map((key) => (
                                <Option key={key} value={key}>{STRATEGY_LABELS[key]}</Option>
                            ))}
                        </Select>
                        <FormHelperText>
                            {isAuto
                                ? 'Ставка пересчитывается автоматически раз в час в пределах недельного бюджета'
                                : 'Ставка остаётся такой, какую вы задали'}
                        </FormHelperText>
                    </FormControl>
                </Stack>
            </Sheet>

            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 1.5 }}>Бюджет и ставки</Typography>

                <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Дневной бюджет, ₽</FormLabel>
                            <Input
                                type="number"
                                value={v.daily_budget ?? ''}
                                onChange={(e) => set({ daily_budget: e.target.value })}
                                placeholder={String(minDaily || 300)}
                            />
                            <FormHelperText>минимум {formatMoney(minDaily)} ₽</FormHelperText>
                        </FormControl>

                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Общий бюджет, ₽</FormLabel>
                            <Input
                                type="number"
                                value={v.total_budget ?? ''}
                                onChange={(e) => set({ total_budget: e.target.value })}
                                placeholder="без ограничения"
                            />
                            <FormHelperText>кампания завершится при достижении</FormHelperText>
                        </FormControl>
                    </Stack>

                    {isAuto && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Недельный бюджет, ₽</FormLabel>
                                <Input
                                    type="number"
                                    value={v.weekly_budget ?? ''}
                                    onChange={(e) => set({ weekly_budget: e.target.value })}
                                />
                                <FormHelperText>обязателен для автостратегии</FormHelperText>
                            </FormControl>

                            {isCpc && (
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Потолок средней цены клика, ₽</FormLabel>
                                    <Input
                                        type="number"
                                        value={v.max_avg_cpc ?? ''}
                                        onChange={(e) => set({ max_avg_cpc: e.target.value })}
                                        placeholder="без ограничения"
                                    />
                                    <FormHelperText>стратегия не поднимет ставку выше</FormHelperText>
                                </FormControl>
                            )}
                        </Stack>
                    )}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>
                                {isCpc ? 'Ставка за клик, ₽' : 'Ставка за 1000 показов, ₽'}
                            </FormLabel>
                            <Input
                                type="number"
                                value={(isCpc ? v.bid_cpc : v.bid_cpm) ?? ''}
                                onChange={(e) => set(
                                    isCpc ? { bid_cpc: e.target.value } : { bid_cpm: e.target.value }
                                )}
                                placeholder={String(minBid || '')}
                            />
                            <FormHelperText>
                                минимум {formatMoney(minBid)} ₽ для выбранных площадок
                                {isAuto ? ' · стартовое значение для автостратегии' : ''}
                            </FormHelperText>
                        </FormControl>

                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Расходование бюджета</FormLabel>
                            <Select
                                value={v.pacing || 'standard'}
                                onChange={(_, val) => val && set({ pacing: val })}
                            >
                                <Option value="standard">Равномерно в течение суток</Option>
                                <Option value="accelerated">Как можно быстрее</Option>
                            </Select>
                            <FormHelperText>
                                равномерное распределение даёт охват весь день
                            </FormHelperText>
                        </FormControl>
                    </Stack>
                </Stack>
            </Sheet>

            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 1.5 }}>Период и частота</Typography>

                <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Начало показов</FormLabel>
                            <Input
                                type="datetime-local"
                                value={toLocalInput(v.date_start)}
                                onChange={(e) => set({ date_start: fromLocalInput(e.target.value) })}
                                disabled={locked('date_start')}
                            />
                            <FormHelperText>пусто — сразу после модерации</FormHelperText>
                        </FormControl>

                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Окончание показов</FormLabel>
                            <Input
                                type="datetime-local"
                                value={toLocalInput(v.date_end)}
                                onChange={(e) => set({ date_end: fromLocalInput(e.target.value) })}
                            />
                            <FormHelperText>пусто — без ограничения</FormHelperText>
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Показов на пользователя в сутки</FormLabel>
                            <Input
                                type="number"
                                value={v.freq_per_day ?? ''}
                                onChange={(e) => set({ freq_per_day: e.target.value })}
                                placeholder="5"
                            />
                            <FormHelperText>
                                чаще — навязчиво, реже — не запомнят
                            </FormHelperText>
                        </FormControl>

                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Минимальный интервал, сек</FormLabel>
                            <Input
                                type="number"
                                value={v.freq_interval_sec ?? ''}
                                onChange={(e) => set({ freq_interval_sec: e.target.value })}
                                placeholder="600"
                            />
                            <FormHelperText>между двумя показами одному человеку</FormHelperText>
                        </FormControl>
                    </Stack>

                    {showSchedule && (
                        <Box>
                            <Divider sx={{ my: 1 }} />
                            <FormLabel sx={{ mb: 1 }}>Время показов</FormLabel>
                            <AdScheduleEditor
                                value={v.schedule}
                                onChange={(schedule) => set({ schedule })}
                            />
                        </Box>
                    )}
                </Stack>
            </Sheet>

            {lockedFields.length > 0 && (
                <Alert color="neutral" variant="soft" size="sm">
                    У работающей кампании можно менять бюджет, ставки, расписание и дату
                    окончания. Чтобы изменить остальное, остановите кампанию.
                </Alert>
            )}
        </Stack>
    );
}